/**
 * Client-side mirror of the server `failure_class` vocabulary
 * (apps/server/src/oauth/utils/connect-error-classifier.util.ts). Used to tag
 * partner-gateway connect failures so error tracking alerts on real errors but stays
 * silent on the user backing out (e.g. closing the Phyllo SDK, which rejects
 * with "Connection cancelled"), and so the `social_connect_failed` funnel can
 * separate cancels from genuine failures.
 */
export type ConnectFailureClass =
  | 'user_aborted'
  | 'provider_error'
  | 'config_error'
  | 'token_error'
  | 'network_error'
  | 'no_pages'
  | 'unknown'

/**
 * Classify a client-side partner-connect failure. Every connect step
 * (connect_session / list_pages / sdk_link / select_page) is a genuine error
 * EXCEPT a user cancellation, which the SDK/popup surfaces as a "cancel" message.
 */
export function classifyClientConnectError(err: unknown): ConnectFailureClass {
  const msg = (err instanceof Error ? err.message : String(err)).toLowerCase()
  if (msg.includes('cancel')) return 'user_aborted'
  return 'provider_error'
}

/** True when the failure was the user backing out — gates error-tracking/console noise. */
export function isUserAbortedConnect(failureClass: ConnectFailureClass): boolean {
  return failureClass === 'user_aborted'
}
